import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Loader2, Search, Send, X } from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { StatutBadge } from '../ui/StatutBadge';
import { toast } from '../ui/sonner';
import { cn } from '../../lib/utils';
import { libelle } from '../../lib/libelles';
import { formatDate } from '../../lib/formats';
import { messageErreur } from '../../lib/erreurs';
import { ETAPES_LOT, RETOURS_LOT } from '../../lib/lotsFiltres';
import { lotsAPI, nouvelleCleLot } from '../../lib/lotsApi';

/** Couleur de chaque étape dans la barre d'avancement (teintes du système). */
export const COULEURS_ETAPE = {
  ATTRIBUE: 'bg-muted-foreground/40',
  CHARGEMENT: 'bg-info/50',
  EN_ROUTE: 'bg-info',
  DOUANE_MALI: 'bg-flame/60',
  PARKING: 'bg-flame',
  SORTI_DOUANE: 'bg-primary/60',
  EN_LIVRAISON: 'bg-primary',
  DEPOTE: 'bg-success',
  ANNULE: 'bg-destructive/60',
};

/** Petite barre empilée : un segment par étape, proportionnel au nombre de camions. */
export const BarreEtapes = ({ stages = {}, className }) => {
  const somme = ETAPES_LOT.reduce((total, code) => total + (Number(stages[code]) || 0), 0);
  return (
    <div
      className={cn('flex h-2 w-full overflow-hidden rounded-full bg-muted', className)}
      role="img"
      aria-label={
        somme === 0
          ? 'Aucun camion'
          : ETAPES_LOT.filter((code) => Number(stages[code]) > 0)
              .map((code) => `${libelle('lot_etape', code)} : ${stages[code]}`)
              .join(', ')
      }
    >
      {somme > 0
        ? ETAPES_LOT.filter((code) => Number(stages[code]) > 0).map((code) => (
            <div
              key={code}
              className={COULEURS_ETAPE[code]}
              style={{ width: `${(Number(stages[code]) / somme) * 100}%` }}
              title={`${libelle('lot_etape', code)} : ${stages[code]}`}
            />
          ))
        : null}
    </div>
  );
};

const PastilleFiltre = ({ code, texte, nombre, couleur, choisie, onChoisir, testid }) => (
  <button
    type="button"
    onClick={onChoisir}
    aria-pressed={onChoisir ? choisie : undefined}
    disabled={!onChoisir}
    className={cn(
      'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors',
      choisie ? 'border-primary bg-primary/10 text-foreground' : 'border-border bg-card text-muted-foreground hover:text-foreground',
      !onChoisir && 'cursor-default',
    )}
    data-testid={testid}
    data-code={code}
  >
    <span className={cn('h-2 w-2 rounded-full', couleur)} aria-hidden="true" />
    {texte}
    <span className="tabular-nums text-foreground">{nombre}</span>
  </button>
);

/** Pastilles des étapes non vides : un clic filtre (ou ouvre) cette étape. */
export const PastillesEtapes = ({ stages = {}, actif = '', onChoisir, className }) => (
  <div className={cn('flex flex-wrap items-center gap-1.5', className)} data-testid="lot-etapes">
    {ETAPES_LOT.filter((code) => Number(stages[code]) > 0 || code === actif).map((code) => (
      <PastilleFiltre
        key={code}
        code={code}
        texte={libelle('lot_etape', code)}
        nombre={Number(stages[code]) || 0}
        couleur={COULEURS_ETAPE[code]}
        choisie={actif === code}
        onChoisir={onChoisir ? () => onChoisir(actif === code ? '' : code) : undefined}
        testid={`lot-etape-${code}`}
      />
    ))}
  </div>
);

/** Couleur de chaque état de retour. */
export const COULEURS_RETOUR = {
  A_RENDRE: 'bg-flame',
  EN_RETOUR: 'bg-info',
  RENDU: 'bg-success',
};

/** Pastilles des retours non vides (à rendre, en retour, rendus). */
export const PastillesRetours = ({ returns = {}, actif = '', onChoisir, className }) => (
  <div className={cn('flex flex-wrap items-center gap-1.5', className)} data-testid="lot-retours">
    {RETOURS_LOT.filter((code) => Number(returns[code]) > 0 || code === actif).map((code) => (
      <PastilleFiltre
        key={code}
        code={code}
        texte={libelle('lot_retour', code)}
        nombre={Number(returns[code]) || 0}
        couleur={COULEURS_RETOUR[code]}
        choisie={actif === code}
        onChoisir={onChoisir ? () => onChoisir(actif === code ? '' : code) : undefined}
        testid={`lot-retour-${code}`}
      />
    ))}
  </div>
);

/** Étape d'un camion de lot, avec « parking » si le camion y est arrêté. */
export const EtapeLigne = ({ ligne }) => (
  <span className="inline-flex flex-wrap items-center gap-1">
    <StatutBadge domaine="lot_etape" code={ligne.stage} />
    {ligne.parked && ligne.stage !== 'PARKING' ? (
      <Badge variant="outline" className="text-xs">
        {libelle('lot_etape', 'PARKING')}
      </Badge>
    ) : null}
  </span>
);

/** Retour du camion vide : pastille seulement une fois dépoté. */
export const RetourLigne = ({ ligne }) =>
  ligne.return_status ? <StatutBadge domaine="lot_retour" code={ligne.return_status} /> : <span>—</span>;

/** Étape et, dessous, le retour du camion vide quand il y en a un : une seule colonne « Étape ». */
export const EtapeRetourLigne = ({ ligne }) => (
  <span className="inline-flex flex-col items-start gap-1">
    <EtapeLigne ligne={ligne} />
    {ligne.return_status ? <StatutBadge domaine="lot_retour" code={ligne.return_status} /> : null}
  </span>
);

/** Tracteur et, dessous, sa citerne : une seule colonne « Camion » pour les deux plaques. */
export const CamionLigne = ({ ligne }) => (
  <span className="inline-flex flex-col leading-tight">
    <span className="whitespace-nowrap font-mono font-semibold">{ligne.tractor_plate}</span>
    {ligne.tank_plate ? (
      <span className="whitespace-nowrap font-mono text-xs text-muted-foreground">{ligne.tank_plate}</span>
    ) : null}
    {ligne.product ? (
      <span className="whitespace-nowrap text-xs text-muted-foreground">{libelle('fuelProduct', ligne.product)}</span>
    ) : null}
  </span>
);

/** Date de chargement et, dessous, date de sortie douane : une seule colonne pour les deux. */
export const DatesLigne = ({ ligne }) => (
  <span className="inline-flex flex-col leading-tight tabular-nums">
    <span className="whitespace-nowrap">{formatDate(ligne.loading_date)}</span>
    <span className="whitespace-nowrap text-muted-foreground">{formatDate(ligne.customs_exit_date)}</span>
  </span>
);

/** En-tête de la colonne des deux dates, une ligne par date, dans l'ordre de la cellule. */
export const EnteteDates = () => (
  <>
    <span className="block">Chargement</span>
    <span className="block">Sortie douane</span>
  </>
);

/** Chauffeur : son nom, sinon son téléphone (un nom absent ne se signale pas). */
export const ChauffeurLigne = ({ ligne }) => <span className="whitespace-nowrap">{ligne.driver_label || '—'}</span>;

/** Lien vers le lot d'une ligne. */
export const LienLot = ({ lotId, reference, className }) =>
  lotId ? (
    <Link
      to={`/lots/${lotId}`}
      onClick={(event) => event.stopPropagation()}
      className={cn('font-mono font-semibold text-primary underline-offset-2 hover:underline', className)}
    >
      {reference || 'Lot'}
    </Link>
  ) : (
    <span>{reference || '—'}</span>
  );

/** Lien vers le trajet d'une ligne confirmée : une icône, le libellé est dans l'infobulle. */
export const LienTrajet = ({ tripId }) =>
  tripId ? (
    <Link
      to={`/trips/${tripId}`}
      onClick={(event) => event.stopPropagation()}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-primary transition-colors hover:bg-muted"
      aria-label="Voir le trajet"
      title="Voir le trajet"
      data-testid="lot-lien-trajet"
    >
      <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
    </Link>
  ) : (
    <span className="text-muted-foreground">—</span>
  );

/**
 * Refus renvoyés par le serveur, avec leur phrase en français : une plaque,
 * puis pourquoi. `lignes` = `[{ plaque, message }]`.
 */
export const ListeRefus = ({ titre, lignes = [], ton = 'attention', className }) => {
  if (lignes.length === 0) return null;
  return (
    <div
      className={cn(
        'rounded-lg border p-3 text-sm',
        ton === 'attention' ? 'border-flame/30 bg-flame/10' : 'border-border bg-muted/40',
        className,
      )}
      data-testid="lot-refus"
    >
      <p className="mb-1 font-medium text-foreground">{titre}</p>
      <ul className="max-h-40 space-y-0.5 overflow-y-auto text-muted-foreground">
        {lignes.map((ligne, index) => (
          <li key={`${ligne.plaque || 'ligne'}-${index}`}>
            {ligne.plaque ? <span className="font-mono font-semibold text-foreground">{ligne.plaque}</span> : null}
            {ligne.plaque ? ' : ' : ''}
            {ligne.message}
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Bouton « Envoyer au responsable » : la liste filtrée part en PDF sur WhatsApp. */
export const BoutonEnvoiResponsable = ({ params, disabled = false, testid = 'lots-send-manager', className }) => {
  const [enCours, setEnCours] = useState(false);
  const cle = useRef(null);

  const envoyer = async () => {
    if (enCours) return;
    // Même clé tant que l'envoi n'a pas abouti : un nouvel essai après une
    // coupure ne part pas deux fois.
    if (!cle.current) cle.current = nouvelleCleLot('lots-envoi');
    setEnCours(true);
    try {
      const resultat = await lotsAPI.sendTrucksList(params, cle.current);
      cle.current = null;
      const nombre = Number(resultat?.recipients) || 0;
      toast.success(nombre > 1 ? `Liste envoyée à ${nombre} responsables` : 'Liste envoyée au responsable');
    } catch (err) {
      toast.error(messageErreur(err, "La liste n'a pas pu partir"));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={envoyer}
      disabled={disabled || enCours}
      className={className}
      data-testid={testid}
    >
      {enCours ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <Send className="h-4 w-4" aria-hidden="true" />
      )}
      Envoyer au responsable
    </Button>
  );
};

/** Tuile de comptage cliquable (tableau de bord). */
export const TuileCompteur = ({ libelleTuile, valeur, couleur, onClick, testid, actif = false }) => {
  const Composant = onClick ? 'button' : 'div';
  return (
    <Composant
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={testid}
      aria-pressed={onClick ? actif : undefined}
      className={cn(
        'flex min-w-[6.5rem] flex-1 flex-col items-start gap-0.5 rounded-lg border bg-card px-3 py-2 text-left transition-colors',
        onClick && 'hover:border-primary/40 hover:bg-accent',
        actif ? 'border-primary' : 'border-border',
      )}
    >
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {couleur ? <span className={cn('h-2 w-2 rounded-full', couleur)} aria-hidden="true" /> : null}
        {libelleTuile}
      </span>
      <span className="text-xl font-semibold tabular-nums text-foreground">{valeur}</span>
    </Composant>
  );
};

/**
 * Résultat d'une écriture groupée (confirmation, retours, ajout) : une phrase,
 * puis les camions refusés avec le message du serveur. Se ferme d'un clic.
 * `resume` vient de `lib/lotsResultats.js`.
 */
export const PanneauResultat = ({ resume, onFermer, className }) => {
  if (!resume) return null;
  const attention = resume.gravite !== 'succes';
  return (
    <section
      className={cn(
        'rounded-lg border px-3 py-2 text-sm',
        attention ? 'border-flame/30 bg-flame/10' : 'border-success/25 bg-success/10',
        className,
      )}
      role="status"
      data-testid="lot-resultat"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-foreground">{resume.titre}</p>
        {onFermer ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="-mr-1 -mt-1 h-7 w-7"
            onClick={onFermer}
            aria-label="Fermer le résultat"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        ) : null}
      </div>
      {resume.refus.length > 0 ? (
        <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto text-muted-foreground">
          {resume.refus.map((ligne, index) => (
            <li key={`${ligne.plaque || 'ligne'}-${index}`}>
              {ligne.plaque ? <span className="font-mono font-semibold text-foreground">{ligne.plaque}</span> : null}
              {ligne.plaque ? ' : ' : ''}
              {ligne.message}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

/** Recherche étroite d'une barre de filtres, avec sa croix. */
export const ChampRecherche = ({ valeur, onChange, placeholder, label, testid, className }) => (
  <div className={cn('relative w-full sm:w-64', className)}>
    <Search
      className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70"
      aria-hidden="true"
    />
    <Input
      type="text"
      placeholder={placeholder}
      aria-label={label}
      value={valeur}
      onChange={(evenement) => onChange(evenement.target.value)}
      className="input-base pl-9 pr-9 text-sm"
      data-testid={testid}
    />
    {valeur ? (
      <button
        type="button"
        onClick={() => onChange('')}
        aria-label="Effacer la recherche"
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 hover:bg-muted"
      >
        <X className="h-4 w-4 text-muted-foreground/70" aria-hidden="true" />
      </button>
    ) : null}
  </div>
);
